import { z } from 'zod';
import { assertNever } from './exhaustive';
import {
  type FieldType,
  type FilterOperator,
  filterOperatorSchema,
  isNullaryOperator,
  supportsOperator,
} from './field-types';

/**
 * INV-6 ONE FILTER SEMANTICS.
 *
 * This is THE filter language. Two backends consume it and no third one may
 * ever exist: a SQL compiler on the server and a JS evaluator on the client for
 * optimistic updates. A shared conformance corpus runs against both in CI
 * (Phase 2). Dashboards and group-by use the same AST.
 *
 * AGGREGATION IS SQL-ONLY IN EVERY PRODUCTION BACKEND - INV-6's amendment
 * (`ARCHITECTURE.md`, approved by Arif 2026-08-16;
 * `docs/decisions/group-by-aggregation.md` D6 and D28). Grouping and aggregates
 * are not filters and have no node in this file; the FILTER half of a grouped
 * read is this AST, compiled by the same compiler, in the same fragment. There
 * is no second PRODUCTION implementation of grouping, because a group is a
 * property of a SET and every server-side caller of the JS evaluator asks about
 * ONE record. `packages/web/src/data/fixtures/FixtureClient.ts` is a named TEST
 * DOUBLE, not a backend; D28 bounds what it may answer and what it must refuse.
 *
 * A CONDITION THAT CROSSES A TABLE IS SQL-ONLY, AND THE JS EVALUATOR REFUSES IT BY
 * NAME - INV-6's second amendment (`ARCHITECTURE.md`, approved by Arif 2026-08-25;
 * `docs/decisions/relation-filters.md` D14, D15). The `related` node IS part of this
 * one AST and is stored, validated and compiled through the same path as every other
 * node; it is executed by the SQL compiler alone, because the JS evaluator's entire
 * input is one record's values and no caller of it holds the second table. It does not
 * fall through and it does not guess: `evaluateFilter` throws `FilterEvaluationError`,
 * the conformance corpus asserts that throw for every related case, and each of the
 * evaluator's five production callers already has a decided behaviour for a throw.
 * There is still no third evaluator.
 *
 * Conditions key on `fieldId` (a metadata UUID), never on a field name - names
 * are mutable (INV-7).
 */

export interface FieldCondition {
  readonly kind: 'condition';
  readonly fieldId: string;
  readonly operator: FilterOperator;
  /** Absent for nullary operators (`isEmpty`, `isNotEmpty`). */
  readonly value?: unknown;
}

export type FilterCombinator = 'and' | 'or';

export interface FilterGroup {
  readonly kind: 'group';
  readonly combinator: FilterCombinator;
  readonly children: readonly FilterNode[];
  /**
   * Inverts the whole group. NOT lives here rather than as a third combinator
   * because `{combinator:'not', children:[a,b]}` has no obvious meaning, while
   * "negate this AND/OR" has exactly one.
   *
   * Note that NOT is a set complement over the RESULT SET, not per condition: a row
   * whose field is NULL does not match `eq`, so it DOES match `not(eq)`. That is the
   * only place where the "NULL matches nothing but isEmpty" rule can be escaped, and
   * it is escaped deliberately - a complement that quietly dropped empty rows would
   * not be a complement.
   */
  readonly negate?: boolean | undefined;
}

/**
 * D3: `any` and `none`; `count` added by D28 after the owner overruled owner question 3.
 * `all` is NOT here, and the reason is empirical rather than aesthetic: an ALL quantifier
 * is `not exists (child where not P)`, which is TRUE for a parent with zero children, so
 * "agencies where all deals are won" would return every agency that never had a deal.
 * Linear documents exactly that surprise in its own filtering guide.
 */
export const RELATION_QUANTIFIERS = ['any', 'none', 'count'] as const;
export type RelationQuantifier = (typeof RELATION_QUANTIFIERS)[number];

export const RELATION_DIRECTIONS = ['forward', 'reverse'] as const;
export type RelationDirection = (typeof RELATION_DIRECTIONS)[number];

/**
 * The comparators a count may carry. A SUBSET of `FILTER_OPERATORS` rather than a new
 * vocabulary - the `as const satisfies` trick `ENUMERABLE_OPERATORS` uses - so a
 * comparator that stops being a filter operator stops compiling here (D28).
 */
export const RELATION_COUNT_COMPARATORS = [
  'eq',
  'neq',
  'lt',
  'lte',
  'gt',
  'gte',
] as const satisfies readonly FilterOperator[];
export type RelationCountComparator = (typeof RELATION_COUNT_COMPARATORS)[number];

export interface RelationCount {
  readonly cmp: RelationCountComparator;
  /** Non-negative integer. A count has no fractional value and no negative one. */
  readonly n: number;
}

/**
 * D1: THE THIRD NODE KIND. A condition on the records this one LINKS TO.
 *
 * THE KEY IS CALLED `children` FOR ONE REASON, AND IT IS NOT SYMMETRY.
 * `assertRawFilterDepth` walks the RAW payload iteratively and follows exactly one key -
 * `children`. Naming the nested filter `where` or `conditions` would put an unbounded
 * tree behind the one check that stands between a hostile payload and a `RangeError`
 * inside zod's `z.lazy` descent. Naming it `children` means the guard covers this kind
 * on the day it lands, with no edit to that function.
 *
 * AN EMPTY `children` IS EXISTENCE, NOT TRUTH, and this is the one asymmetry with
 * `FilterGroup`. An empty group is its combinator's neutral element; an empty `related`
 * node compiles to `exists (select 1 from ...)` and NEVER to `true`, because "agencies
 * with at least one contract" is the headline sentence and it has no inner predicate to
 * carry. The opposite behaviour - an empty relation operand evaporating into "no filter"
 * - is the researched trap this module exists to avoid.
 *
 * `negate` is deliberately absent: a negated related node is `not(related(...))` through
 * the existing group constructor, and D3's `none` quantifier is what the editor uses so
 * it never has to build one. Both toggle the same polarity in D28's monotonicity rule.
 */
export interface RelatedCondition {
  readonly kind: 'related';
  /** A MANY_TO_ONE relation field UUID. D2 says which ones are legal and what each means. */
  readonly anchorFieldId: string;
  readonly quantifier: RelationQuantifier;
  /**
   * SELF-RELATIONS ONLY (D2). An anchor an object declares at ITSELF satisfies both
   * halves of D2's derivation, so the direction cannot be derived and must be stated.
   * Required when the anchor is a self-relation, REFUSED when it is not.
   */
  readonly direction?: RelationDirection | undefined;
  /**
   * COUNT QUANTIFIER ONLY (D28). Required when `quantifier === 'count'` and REFUSED
   * otherwise - the same required-iff rule `direction` follows, enforced in the same
   * places (`resolveAnchor`'s caller, the view validator, `validateFilterAgainstFields`).
   */
  readonly count?: RelationCount | undefined;
  /** The predicate the related record must satisfy, conjoined. EMPTY MEANS EXISTENCE. */
  readonly children: readonly FilterNode[];
}

export type FilterNode = FieldCondition | FilterGroup | RelatedCondition;

export const filterCombinatorSchema = z.literal(['and', 'or']);
export const relationQuantifierSchema = z.literal([...RELATION_QUANTIFIERS]);
export const relationDirectionSchema = z.literal([...RELATION_DIRECTIONS]);
export const relationCountComparatorSchema = z.literal([...RELATION_COUNT_COMPARATORS]);

export const relationCountSchema = z.strictObject({
  cmp: relationCountComparatorSchema,
  n: z.int().min(0),
});

const fieldConditionSchema = z.strictObject({
  kind: z.literal('condition'),
  fieldId: z.uuid(),
  operator: filterOperatorSchema,
  value: z.unknown().optional(),
});

// Recursion needs z.lazy plus an explicit annotation; zod cannot infer a
// self-referential type on its own.
export const filterNodeSchema: z.ZodType<FilterNode> = z.lazy(() =>
  z.discriminatedUnion('kind', [fieldConditionSchema, filterGroupSchema, relatedConditionSchema]),
);

const filterGroupSchema = z.strictObject({
  kind: z.literal('group'),
  combinator: filterCombinatorSchema,
  children: z.array(filterNodeSchema),
  negate: z.boolean().optional(),
});

const relatedConditionSchema = z.strictObject({
  kind: z.literal('related'),
  anchorFieldId: z.uuid(),
  quantifier: relationQuantifierSchema,
  direction: relationDirectionSchema.optional(),
  count: relationCountSchema.optional(),
  children: z.array(filterNodeSchema),
});

/**
 * Nesting depth the parser accepts. A filter arrives as JSON from the browser, an
 * n8n workflow or Oliver; without a bound, a deliberately deep tree is a cheap way
 * to blow the stack of every recursive walk in this file and in both backends.
 */
export const MAX_FILTER_DEPTH = 20;

/**
 * A filter whose STRUCTURE is refused (too deep), as opposed to a value that is refused
 * (`FilterOperandError`).
 *
 * Typed rather than a bare `Error` on purpose: the HTTP error handler maps known error types to a
 * status and answers an unknown one with a flat 500, precisely so that a driver error cannot leak
 * table names into a browser. A guard that throws `Error` therefore produces a 500 for what is
 * plainly a bad request - which is what happened before this type existed.
 */
export class FilterStructureError extends Error {
  override readonly name = 'FilterStructureError';
}

export function parseFilterNode(input: unknown): FilterNode {
  // BEFORE zod, not after: see assertRawFilterDepth.
  assertRawFilterDepth(input);
  const node = filterNodeSchema.parse(input);
  assertFilterDepth(node);
  // D7's rule: a cap is enforced at EVERY entrance or it is enforced at none. The other
  // three are `parseRecordQuery`, `validateViewShape` and `planRelatedNodes`.
  assertRelationHops(node);
  return node;
}

/**
 * Bounds the nesting of a RAW, unvalidated payload - and does it ITERATIVELY.
 *
 * Checking after `zod` has parsed is too late and was verified to be too late: `filterNodeSchema`
 * is a `z.lazy` recursion, so a 5000-level tree overflows the stack INSIDE zod's descent and
 * arrives as a `RangeError` (i.e. a 500) rather than as this readable rejection. A recursive
 * pre-check would overflow in the same place, so the walk carries its own explicit stack and cannot.
 *
 * It only follows `children`, which is the one recursive edge in the grammar.
 */
export function assertRawFilterDepth(raw: unknown, max: number = MAX_FILTER_DEPTH): void {
  const pending: { readonly node: unknown; readonly depth: number }[] = [{ node: raw, depth: 1 }];
  while (pending.length > 0) {
    const entry = pending.pop();
    if (entry === undefined) return;
    if (entry.depth > max) {
      throw new FilterStructureError(`filter is nested deeper than ${max} levels`);
    }
    if (typeof entry.node !== 'object' || entry.node === null) continue;
    const children = (entry.node as { children?: unknown }).children;
    if (!Array.isArray(children)) continue;
    for (const child of children) pending.push({ node: child, depth: entry.depth + 1 });
  }
}

/**
 * Bounds the nesting of an already-parsed tree.
 *
 * The counterpart to `assertRawFilterDepth`, for a tree built in code (the `and`/`or`/`not`
 * constructors) rather than received as JSON. Both exist: the raw check is what protects the
 * network path, this one catches a programmatically assembled monster before it reaches a backend.
 */
export function assertFilterDepth(node: FilterNode, depth = 1): void {
  if (depth > MAX_FILTER_DEPTH) {
    throw new FilterStructureError(`filter is nested deeper than ${MAX_FILTER_DEPTH} levels`);
  }
  // A related node's `children` are a nesting level exactly as a group's are: the raw
  // guard already counts them (it follows the key, not the kind), and this one must
  // agree or a tree built in code would pass a bound the same tree fails as JSON.
  if (node.kind === 'group' || node.kind === 'related') {
    for (const child of node.children) assertFilterDepth(child, depth + 1);
  }
}

/**
 * D7: HOPS, counted in `related` nodes on a path from the root.
 *
 * A different quantity from `MAX_FILTER_DEPTH`, which bounds TOTAL nesting: a filter can
 * be nineteen groups deep and reach no second table at all, and it can be three levels
 * deep and cross three tables. Both caps are needed and both are enforced.
 *
 * TWO rather than ONE, because with junctions as real objects (INV-9) an N:N traversal
 * lands IN the junction: the junction's own fields are hop 1 and the far object's fields
 * are hop 2, so a one-hop cap would ship the N:N filter half-finished. Three was rejected
 * on cost. Raising it is this constant plus an obligation.
 */
export const MAX_RELATION_HOPS = 2;

export function assertRelationHops(node: FilterNode, hops = 0): void {
  if (node.kind === 'condition') return;
  const next = node.kind === 'related' ? hops + 1 : hops;
  if (next > MAX_RELATION_HOPS) {
    throw new FilterStructureError(
      `a filter can look ${MAX_RELATION_HOPS} relations deep. This one looks ${next}`,
    );
  }
  for (const child of node.children) assertRelationHops(child, next);
}

/**
 * Every fieldId the filter resolves against THIS QUERY'S OWN OBJECT. Used for permission
 * checks and joins.
 *
 * A `related` node contributes NOTHING: its anchor and its inner conditions belong to
 * OTHER objects, so feeding them into a loop that checks ids against one object id would
 * produce a spurious `forbidden` or `invalid_state` on ids that are perfectly legal. The
 * permission checks for those hops run in `planRelatedNodes`, with metadata in hand (D6).
 *
 * THIS CHANGE IS LOAD-BEARING OUTSIDE THE RECORD LAYER. `webhooks/service.ts` `assertScope`
 * uses this function as its only filter check, and before the `related` arm existed a
 * related node reaching that loop threw from the `assertNever` below - loudly, and by
 * accident. That accidental guard disappears here, which is why D25's refusal is written
 * into `assertScope` in the same change.
 */
export function filterFieldIds(node: FilterNode): readonly string[] {
  switch (node.kind) {
    case 'condition':
      return [node.fieldId];
    case 'group':
      return node.children.flatMap(filterFieldIds);
    case 'related':
      return [];
    default:
      return assertNever(node, 'filterFieldIds');
  }
}

/** Every `related` node in the tree, depth first, in walk order. */
export function relatedNodesOf(node: FilterNode): readonly RelatedCondition[] {
  switch (node.kind) {
    case 'condition':
      return [];
    case 'group':
      return node.children.flatMap(relatedNodesOf);
    case 'related':
      return [node, ...node.children.flatMap(relatedNodesOf)];
    default:
      return assertNever(node, 'relatedNodesOf');
  }
}

export interface FilterValidationIssue {
  readonly fieldId: string;
  readonly message: string;
}

/**
 * Structural validation the zod schema cannot express: an operator must be
 * legal for the field's type, and nullary operators must not carry a value.
 * `fieldTypes` comes from the metadata cache at call time.
 */
export function validateFilterAgainstFields(
  node: FilterNode,
  fieldTypes: ReadonlyMap<string, FieldType>,
): readonly FilterValidationIssue[] {
  switch (node.kind) {
    case 'condition': {
      const fieldType = fieldTypes.get(node.fieldId);
      if (fieldType === undefined) {
        return [{ fieldId: node.fieldId, message: 'unknown fieldId' }];
      }
      if (!supportsOperator(fieldType, node.operator)) {
        return [
          {
            fieldId: node.fieldId,
            message: `operator ${node.operator} is not supported for field type ${fieldType}`,
          },
        ];
      }
      if (isNullaryOperator(node.operator) && node.value !== undefined) {
        return [
          { fieldId: node.fieldId, message: `operator ${node.operator} must not carry a value` },
        ];
      }
      if (!isNullaryOperator(node.operator) && node.value === undefined) {
        return [{ fieldId: node.fieldId, message: `operator ${node.operator} requires a value` }];
      }
      return [];
    }
    case 'group':
      return node.children.flatMap((child) => validateFilterAgainstFields(child, fieldTypes));
    /**
     * D26 row 3. Field ids are globally unique (INV-7), so a caller that puts several
     * objects' fields into one map gets correct answers for the inner conditions with no
     * signature change. What this arm adds is the ANCHOR's own rule - it must resolve to
     * a RELATION field - and the count member's required-iff rule, which is exactly the
     * "structural validation the zod schema cannot express" this function exists for.
     */
    case 'related': {
      const issues = [...relatedMemberIssues(node)];
      const anchorType = fieldTypes.get(node.anchorFieldId);
      if (anchorType === undefined) {
        issues.push({ fieldId: node.anchorFieldId, message: 'unknown anchor fieldId' });
      } else if (anchorType !== 'RELATION') {
        issues.push({
          fieldId: node.anchorFieldId,
          message: `a related filter anchors on a RELATION field, and this one is a ${anchorType}`,
        });
      }
      return [
        ...issues,
        ...node.children.flatMap((child) => validateFilterAgainstFields(child, fieldTypes)),
      ];
    }
    default:
      return assertNever(node, 'validateFilterAgainstFields');
  }
}

/**
 * THE COUNT MEMBER'S REQUIRED-IFF RULE (D28), in one place so the two conditional members
 * cannot drift apart. `direction`'s half needs metadata - whether the anchor is a
 * self-relation - so it lives in `resolveAnchor`; this half needs nothing but the node.
 */
export function relatedMemberIssues(node: RelatedCondition): readonly FilterValidationIssue[] {
  if (node.quantifier === 'count' && node.count === undefined) {
    return [
      {
        fieldId: node.anchorFieldId,
        message: 'a count quantifier must carry a comparison, e.g. {cmp: "gt", n: 3}',
      },
    ];
  }
  if (node.quantifier !== 'count' && node.count !== undefined) {
    return [
      {
        fieldId: node.anchorFieldId,
        message: `quantifier ${node.quantifier} does not take a count comparison`,
      },
    ];
  }
  return [];
}

// --- constructors (keep call sites readable) --------------------------------

export function condition(
  fieldId: string,
  operator: FilterOperator,
  ...value: [] | [unknown]
): FieldCondition {
  return value.length === 0
    ? { kind: 'condition', fieldId, operator }
    : { kind: 'condition', fieldId, operator, value: value[0] };
}

export function and(...children: readonly FilterNode[]): FilterGroup {
  return { kind: 'group', combinator: 'and', children };
}

export function or(...children: readonly FilterNode[]): FilterGroup {
  return { kind: 'group', combinator: 'or', children };
}

/** NOT (a AND b AND ...). Negating one condition is `not(condition)`. */
export function not(...children: readonly FilterNode[]): FilterGroup {
  return { kind: 'group', combinator: 'and', children, negate: true };
}

export interface RelatedOptions {
  readonly direction?: RelationDirection | undefined;
  readonly count?: RelationCount | undefined;
}

/** A related condition. No children is EXISTENCE, which is not the group's neutral element. */
export function related(
  anchorFieldId: string,
  quantifier: RelationQuantifier,
  children: readonly FilterNode[] = [],
  options: RelatedOptions = {},
): RelatedCondition {
  return {
    kind: 'related',
    anchorFieldId,
    quantifier,
    ...(options.direction === undefined ? {} : { direction: options.direction }),
    ...(options.count === undefined ? {} : { count: options.count }),
    children,
  };
}

// --- D8: the budget, and D28: the canonicaliser -----------------------------

/**
 * D8: CORRELATED SUBQUERY EXECUTIONS ONE REQUEST MAY CAUSE. The number a person can
 * picture.
 *
 * It is a budget of EXECUTIONS rather than of nodes, because the same compiled predicate
 * is run more than once by two of the three read shapes: `withTotalCount` runs it a
 * second time for the count, and a grouped read runs it four times (currency codes, the
 * group page, the group total, the seeded rows). Budgeting nodes made five related
 * conditions on a board twenty subquery executions.
 */
export const RELATED_EXECUTION_BUDGET = 10;

/** How many times a read shape executes the SAME compiled predicate. */
export type ReadShape = 'flat' | 'flatWithTotal' | 'grouped';

const EXECUTIONS: Readonly<Record<ReadShape, number>> = {
  flat: 1,
  flatWithTotal: 2,
  grouped: 4,
};

/** flat 10, flatWithTotal 5, grouped 2 - derived, never written down a second time. */
export const relatedBudgetFor = (shape: ReadShape): number =>
  Math.floor(RELATED_EXECUTION_BUDGET / EXECUTIONS[shape]);

/**
 * D28: the budget is a COST, not a node count. An existence check is a semi-join that
 * stops at the first matching row; a count reads every matching row per parent AND costs
 * the outer relation its join strategy (measured: `exists` plans as a hash semi join, the
 * same predicate written `(select count(*) ...) > 0` plans as a sequential scan with a
 * per-row subplan). Three is deliberately an UNDER-statement of an unbounded ratio,
 * chosen so a list can still carry one count beside two existence checks with a total on,
 * and a board carries none.
 */
export const RELATED_NODE_COST: Readonly<Record<RelationQuantifier, number>> = {
  any: 1,
  none: 1,
  count: 3,
};

/**
 * THE BUDGET RULE, STATED ONCE (D8 v4). Every other mention refers here.
 *
 *   1. CANONICALISE first (D28). `count gt 0` is `any` before it is priced, so it costs 1
 *      and not 3. The order is part of the rule: three `count gt 0` nodes cost 9 before
 *      canonicalisation and 3 after, which flips accept to refuse against a cap of 5.
 *   2. totalCost = sum of RELATED_NODE_COST[quantifier] over EVERY related node in the
 *      tree, at any depth, AFTER step 1.
 *   3. REFUSE unless totalCost <= relatedBudgetFor(shape).
 *
 * This function performs steps 1 and 2. Step 3 belongs to the caller, which is the layer
 * that knows its read shape and owns the refusal's error type.
 */
export function totalRelatedCost(node: FilterNode): number {
  return relatedNodesOf(node).reduce(
    (sum, related) => sum + RELATED_NODE_COST[canonicaliseRelationCount(related).quantifier],
    0,
  );
}

/** The cost of one filter, broken down so a refusal can say where the number came from. */
export interface RelatedCostBreakdown {
  readonly cost: number;
  readonly counts: number;
  readonly checks: number;
}

export function relatedCostBreakdown(node: FilterNode): RelatedCostBreakdown {
  const canonical = relatedNodesOf(node).map((related) => canonicaliseRelationCount(related));
  const counts = canonical.filter((entry) => entry.quantifier === 'count').length;
  const checks = canonical.length - counts;
  return {
    cost: counts * RELATED_NODE_COST.count + checks * RELATED_NODE_COST.any,
    counts,
    checks,
  };
}

/**
 * The section-11 sentence for a filter that is over its shape's budget. The parenthetical
 * is GENERATED from the node breakdown, so the user can see where the number came from -
 * and so a refusal cannot name a condition COUNT while the rule sums COST.
 */
export function relatedBudgetMessage(shape: ReadShape, node: FilterNode): string {
  const { cost, counts, checks } = relatedCostBreakdown(node);
  const parts: string[] = [];
  if (counts > 0) {
    parts.push(
      counts === 1
        ? `one count (${RELATED_NODE_COST.count})`
        : `${counts} counts (${RELATED_NODE_COST.count} each)`,
    );
  }
  if (checks > 0) {
    parts.push(
      checks === 1
        ? `one check for related records (${RELATED_NODE_COST.any})`
        : `${checks} checks for related records (${RELATED_NODE_COST.any} each)`,
    );
  }
  const breakdown = parts.join(' and ');
  const budget = relatedBudgetFor(shape);
  if (shape === 'grouped') {
    return `a grouped view runs its filter four times, so its related-record filters can cost at most ${budget}. This one costs ${cost}: ${breakdown}`;
  }
  return `related-record filters in this view can cost at most ${budget}. This one costs ${cost}: ${breakdown}`;
}

/** The canonical shape of a related node's quantifier and count, after D28's table. */
export interface CanonicalRelation {
  readonly quantifier: RelationQuantifier;
  readonly count?: RelationCount | undefined;
}

/**
 * D28: THE COLLAPSE CASES ARE CANONICALISED rather than left as two spellings.
 *
 *   gt 0, gte 1, neq 0  -> any    (identical truth value, and `exists` short-circuits)
 *   eq 0, lt 1, lte 0   -> none   (identical truth value, and `not exists` short-circuits)
 *   gte 0               -> REFUSED, true for every parent
 *   lt 0                -> REFUSED, true for no parent
 *   everything else     -> kept as `count`
 *
 * PURE, and it has three callers - the view validator's `checkAndNormalise`,
 * `planRelatedNodes` for the direct API path, and the editor's `toViewFilter` - which is
 * the same "one pure function, every door" discipline `resolveAnchor` follows. A stored
 * view is therefore always canonical, so two views that mean the same thing are the same
 * STORED bytes. (Not the same cursor: `predicateDigest` folds the RAW filter, so a direct
 * API caller who sends `count gt 0` and one who sends `any` get two cursors for one
 * compiled predicate. Each walk is internally consistent.)
 */
export function canonicaliseRelationCount(node: {
  readonly quantifier: RelationQuantifier;
  readonly count?: RelationCount | undefined;
}): CanonicalRelation {
  if (node.quantifier !== 'count' || node.count === undefined) {
    return { quantifier: node.quantifier };
  }
  const { cmp, n } = node.count;
  if ((cmp === 'gt' && n === 0) || (cmp === 'gte' && n === 1) || (cmp === 'neq' && n === 0)) {
    return { quantifier: 'any' };
  }
  if ((cmp === 'eq' && n === 0) || (cmp === 'lt' && n === 1) || (cmp === 'lte' && n === 0)) {
    return { quantifier: 'none' };
  }
  if (cmp === 'gte' && n === 0) {
    throw new FilterStructureError(
      '"at least 0" is true for every record. Pick a number above zero, or use "has any"',
    );
  }
  if (cmp === 'lt' && n === 0) {
    throw new FilterStructureError(
      '"fewer than 0" is true for no record. Pick a number above zero, or use "has none"',
    );
  }
  return { quantifier: 'count', count: node.count };
}

/** The canonicaliser applied to a whole tree, in place, keeping every other member. */
export function canonicaliseRelatedNodes(node: FilterNode): FilterNode {
  switch (node.kind) {
    case 'condition':
      return node;
    case 'group':
      return { ...node, children: node.children.map(canonicaliseRelatedNodes) };
    case 'related': {
      const canonical = canonicaliseRelationCount(node);
      const { count: _dropped, ...rest } = node;
      return {
        ...rest,
        quantifier: canonical.quantifier,
        ...(canonical.count === undefined ? {} : { count: canonical.count }),
        children: node.children.map(canonicaliseRelatedNodes),
      };
    }
    default:
      return assertNever(node, 'canonicaliseRelatedNodes');
  }
}

/**
 * D28's canonicalisation table, EXPORTED as data so the server tests and the editor test
 * (a different runner, in `packages/web`) assert the same rows rather than two tables
 * somebody maintains by hand. Obligation 62.
 */
export interface RelationCanonicalCase {
  readonly cmp: RelationCountComparator;
  readonly n: number;
  /** The quantifier the node canonicalises to, or `refused` for the two that cannot narrow. */
  readonly canonical: RelationQuantifier | 'refused';
}

export const RELATION_CANONICAL_CASES: readonly RelationCanonicalCase[] = [
  { cmp: 'gt', n: 0, canonical: 'any' },
  { cmp: 'gte', n: 1, canonical: 'any' },
  { cmp: 'neq', n: 0, canonical: 'any' },
  { cmp: 'eq', n: 0, canonical: 'none' },
  { cmp: 'lt', n: 1, canonical: 'none' },
  { cmp: 'lte', n: 0, canonical: 'none' },
  { cmp: 'gte', n: 0, canonical: 'refused' },
  { cmp: 'lt', n: 0, canonical: 'refused' },
  { cmp: 'gt', n: 3, canonical: 'count' },
  { cmp: 'lte', n: 2, canonical: 'count' },
  { cmp: 'eq', n: 1, canonical: 'count' },
];

/**
 * ONE ENTRY OF THE PUBLISHED ANCHOR SET (D27), as it travels on the wire.
 *
 * It lives in `shared` rather than beside `resolveAnchor` because both consumers are
 * browser code - the filter editor's anchor select and `pruneFilter`'s anchor validation -
 * and `packages/web` must never import from `packages/server`. The RULE that decides which
 * fields are in the set stays on the server, in one place; this is only its shape.
 */
export interface FilterAnchor {
  readonly anchorFieldId: string;
  readonly anchorFieldName: string;
  readonly anchorFieldLabel: string;
  readonly relatedObjectId: string;
  readonly relatedObjectName: string;
  /** `ambiguous` for a self-relation, which is the one direction that cannot be derived. */
  readonly direction: RelationDirection | 'ambiguous';
  /** What the picker prints. */
  readonly label: string;
  /**
   * THE SAME SUBJECT IN THE SINGULAR (wave 2, D21/D28's chip sentences).
   *
   * `label` is the related object's PLURAL on a reverse hop, which is right for "Has any
   * Deals" and wrong for "Has exactly 1 Deal" - and section 11 of the decision spells both
   * of those out verbatim. A browser cannot derive one from the other in any language, so
   * the server sends both rather than the editor guessing at an "s".
   */
  readonly labelSingular: string;
  /**
   * AND THE PLURAL, for the sentences that name an OBJECT rather than a hop. D11's banner
   * and D28's C4 sentence both name an object the reader did NOT pick, where `label` -
   * which is the hop's own subject and is singular on a forward hop - would read as one
   * record instead of as the object.
   */
  readonly labelPlural: string;
}
